
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BarChart3, 
  TrendingUp, 
  Award, 
  Users,
  Calendar,
  Target
} from 'lucide-react';

interface CertificateAnalyticsProps {
  stats: {
    totalIssued: number;
    recentlyIssued: number;
    pendingGeneration: number;
    averageScore: number;
  } | null;
}

const CertificateAnalytics: React.FC<CertificateAnalyticsProps> = ({ stats }) => {
  if (!stats) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-40 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const completionRate = stats.totalIssued / (stats.totalIssued + stats.pendingGeneration) * 100;
  const weeklyGrowth = stats.recentlyIssued > 0 ? ((stats.recentlyIssued / stats.totalIssued) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completion Rate</p>
                <p className="text-2xl font-bold">{completionRate.toFixed(1)}%</p>
              </div>
              <Target className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Weekly Growth</p>
                <p className="text-2xl font-bold">{weeklyGrowth.toFixed(1)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Quality Score</p>
                <p className="text-2xl font-bold">{stats.averageScore}%</p>
              </div>
              <Award className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Efficiency</p>
                <p className="text-2xl font-bold">
                  {stats.pendingGeneration === 0 ? '100%' : ((stats.totalIssued / (stats.totalIssued + stats.pendingGeneration)) * 100).toFixed(1) + '%'}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Certificate Generation Trends
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Total Certificates</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-muted rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: '100%' }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">{stats.totalIssued}</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm">This Week</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-muted rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min(100, (stats.recentlyIssued / Math.max(stats.totalIssued, 1)) * 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">{stats.recentlyIssued}</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm">Pending</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-muted rounded-full h-2">
                    <div 
                      className="bg-orange-600 h-2 rounded-full"
                      style={{ width: `${Math.min(100, (stats.pendingGeneration / Math.max(stats.totalIssued + stats.pendingGeneration, 1)) * 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">{stats.pendingGeneration}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Quality Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Average Score</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-muted rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${stats.averageScore}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">{stats.averageScore}%</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm">Pass Threshold</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-muted rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: '85%' }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">85%</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm">Quality Rating</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-muted rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min(100, (stats.averageScore / 85) * 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">
                    {stats.averageScore >= 95 ? 'Excellent' : 
                     stats.averageScore >= 90 ? 'Very Good' : 
                     stats.averageScore >= 85 ? 'Good' : 'Needs Improvement'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Performance Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{completionRate.toFixed(0)}%</div>
              <div className="text-sm text-green-700">Certificate Success Rate</div>
              <div className="text-xs text-muted-foreground mt-1">
                {stats.totalIssued} of {stats.totalIssued + stats.pendingGeneration} eligible
              </div>
            </div>
            
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{weeklyGrowth.toFixed(1)}%</div>
              <div className="text-sm text-blue-700">Weekly Growth Rate</div>
              <div className="text-xs text-muted-foreground mt-1">
                {stats.recentlyIssued} certificates this week
              </div>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{stats.averageScore}%</div>
              <div className="text-sm text-purple-700">Average Achievement</div>
              <div className="text-xs text-muted-foreground mt-1">
                {stats.averageScore >= 85 ? 'Above threshold' : 'Below threshold'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CertificateAnalytics;
